import { onBecomeObserved, onBecomeUnobserved } from 'mobx';
import agent from '../../services/api/agent';
import aiMediaAgent from '../../services/api/aiMediaAgent';
import { createAiMediaConnection } from '../../services/api/creatorHubConnection';
import type { IContent, IPagePlaceholder } from '../../services/models/PageViewModel';
import type { IPropertyAiData } from '../../services/models/PropertyViewModel';
import { store } from '../store';
import MediaGenerationStore from './mediaGenerationStore';

/**
 * The composition root for media generation, mirroring `createAiSessionStore`: the store holds its
 * collaborators through ports only, and the wiring that hands it real ones lives here, outside all
 * of them. The connection's envelopes are handed to the store here as well, outside both modules,
 * the way `createAiSessionStore` does it. Digging the AI prompt out of the property tree is part of
 * that wiring, which is why the store's port asks for the prompt to be saved and never sees the tree.
 */

/**
 * The same fetch the double-click edit flow makes, then the same write the property panel makes.
 * The AI properties come back as their own top-level `aiData` group — the properties endpoint puts
 * every group flat on the response, never nested inside another group's items.
 */
const saveAiProperty = async (content: IContent, propertyReactName: keyof IPropertyAiData, value: string): Promise<void> => {
  const properties = await agent.Page.GetPagePlaceholderContentProperties(content.courseID, content.coursePageID, content.pagePlaceholderID, content.contentID);
  const property = properties?.aiData?.items?.[propertyReactName];
  if (!property) {
    console.warn(`The content has no ${propertyReactName} property to save the media generation value to`, content.contentID);
    return;
  }

  await store.propertyStore.setOption(property, value);
};

const savePrompt = (content: IContent, prompt: string): Promise<void> => saveAiProperty(content, 'aiPrompt', prompt);

const saveStatusProjection = (content: IContent, statusProjection: string): Promise<void> => saveAiProperty(content, 'aiInternalData', statusProjection);

/**
 * The empty image content the media-menu flow commits to, created the way the media library and
 * Unsplash create theirs — the page store's own `createImage`, with no source to put in it yet.
 *
 * That method puts the content in the page store's *selected* placeholder, which clicking the
 * toolbox already made this one. The dialog stands open in between, though, so the selection is
 * made good here rather than assumed: a content created in the wrong placeholder would be an image
 * appearing somewhere the author was not looking.
 *
 * What comes back is the content the page is rendering, not the one `createImage` returns. Those are
 * two objects: pushing onto an observable array stores an observable proxy of what it was handed and
 * hands the raw object back, so every write the generation makes to that one — the marker, the
 * generating projection — changes the value without ever telling the page to redraw. The author sees
 * nothing happen until something else re-renders the slot.
 */
const createImageContent = async (placeholder: IPagePlaceholder): Promise<IContent | null> => {
  if (store.pageStore.selectedPlaceholder?.pagePlaceholderID !== placeholder.pagePlaceholderID) await store.pageStore.selectPlaceholder(placeholder);

  const created = await store.pageStore.createImage('');
  if (!created) return null;

  return getLoadedContents().find((loaded) => loaded.contentID === created.contentID) ?? created;
};

/**
 * Every content on the page the author has open, children included — placeholders nest, and a
 * generated image is as likely to sit inside a layout as at the top level. Which of them a terminal
 * event names is the store's call; this only hands over the page.
 */
const contentsOf = (placeholders: IPagePlaceholder[]): IContent[] => placeholders.flatMap((placeholder) => [...placeholder.contents, ...contentsOf(placeholder.placeholderChildren ?? [])]);

const getLoadedContents = (): IContent[] => contentsOf(store.pageStore.coursePage?.placeholders ?? []);

export const createMediaGenerationStore = (): MediaGenerationStore => {
  const connection = createAiMediaConnection();

  const mediaGenerationStore = new MediaGenerationStore({
    agent: aiMediaAgent,
    connection,
    pageCallbacks: {
      // The property store decides whether the value row has to be created or updated, and backfills
      // the new id; going around it would put that decision in a second place.
      savePrompt,
      saveStatusProjection,
      createImageContent,
      getLoadedContents,
    },
    now: () => Date.now(),
  });

  connection.setHandlers({
    onEnvelope: (envelope) => mediaGenerationStore.applyTerminalEnvelope(envelope),
    onReconnected: () => void mediaGenerationStore.resubscribe(),
  });

  // The display clock ticks only while something on screen reads it — only branches holding a
  // generating projection do, counting or overdue. A page without one pays nothing.
  let clockHandle: number | undefined;
  onBecomeObserved(mediaGenerationStore, 'nowUtcMs', () => {
    mediaGenerationStore.advanceClock();
    clockHandle = window.setInterval(mediaGenerationStore.advanceClock, 1000);
  });
  onBecomeUnobserved(mediaGenerationStore, 'nowUtcMs', () => window.clearInterval(clockHandle));

  return mediaGenerationStore;
};
